import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Check, ArrowRight, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../lib/api';

const DISMISSED_KEY = 'onboarding_minimized';
const RESTORED_KEY  = 'onboarding_restored';

const OnboardingChecklist = () => {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchStatus(); }, []);

  // Determine initial minimized state once data is loaded.
  // Auto-collapse when progress > 50 % (unless user has explicitly restored).
  useEffect(() => {
    if (!status) return;
    const wasMinimized = sessionStorage.getItem(DISMISSED_KEY) === 'true';
    const wasRestored  = sessionStorage.getItem(RESTORED_KEY)  === 'true';
    if (wasMinimized) { setMinimized(true); return; }
    if (status.progress_pct > 50 && !wasRestored) setMinimized(true);
  }, [status]);

  const fetchStatus = async () => {
    try {
      const resp = await api.get('/org/onboarding/status');
      setStatus(resp.data);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMinimize = () => {
    setMinimized(true);
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    sessionStorage.removeItem(RESTORED_KEY);
  };

  const handleRestore = () => {
    setMinimized(false);
    sessionStorage.removeItem(DISMISSED_KEY);
    sessionStorage.setItem(RESTORED_KEY, 'true');
  };

  if (loading || !status) return null;

  const allItems   = [...(status.setup_items || []), ...(status.operational_items || [])];
  const totalCount = allItems.length;

  // Complete — permanent compact badge
  if (status.is_complete) {
    return (
      <div
        className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-slate-200 shadow-sm bg-white w-fit"
        data-testid="onboarding-complete"
      >
        <CheckCircle size={15} className="text-green-500 shrink-0" />
        <span className="text-sm font-semibold text-slate-700">Onboarding klar!</span>
      </div>
    );
  }

  // Minimized — collapsible badge
  if (minimized) {
    return (
      <button
        onClick={handleRestore}
        className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-slate-200 shadow-sm bg-white hover:bg-slate-50 text-sm font-medium transition-colors w-fit"
        data-testid="onboarding-minimized-badge"
      >
        <span className="w-2 h-2 rounded-full bg-slate-400" />
        Onboarding
        <span className="font-bold text-slate-600">{status.progress_pct}%</span>
        <ChevronDown size={14} className="text-slate-400" />
      </button>
    );
  }

  const renderItems = (items, sectionLabel) => {
    if (!items || items.length === 0) return null;
    return (
      <div className="space-y-2">
        {sectionLabel && (
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground px-1">
            {sectionLabel}
          </p>
        )}
        {items.map((item) => {
          const isCompleted = item.status === 'completed';
          return (
            <button
              key={item.key}
              onClick={() => !isCompleted && navigate(item.cta_path)}
              disabled={isCompleted}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
                isCompleted
                  ? 'bg-success/10 cursor-default'
                  : 'bg-muted hover:bg-primary/10 cursor-pointer'
              }`}
              data-testid={`onboarding-item-${item.key}`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                isCompleted ? 'bg-success text-white' : 'bg-muted-foreground/20'
              }`}>
                {isCompleted
                  ? <Check className="w-4 h-4" />
                  : <span className="w-2 h-2 rounded-full bg-muted-foreground/50" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium leading-snug ${
                  isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'
                }`}>
                  {item.title}
                </p>
                {!isCompleted && item.description && (
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.description}</p>
                )}
              </div>
              {!isCompleted && <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
            </button>
          );
        })}
      </div>
    );
  };

  const hasSetup       = status.setup_items && status.setup_items.length > 0;
  const hasOperational = status.operational_items && status.operational_items.length > 0;
  const showSectionLabels = hasSetup && hasOperational;

  return (
    <Card className="border border-slate-200 shadow-sm" data-testid="onboarding-checklist">
      <CardHeader className="border-b pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading font-bold">Kom igång med AIUS Control</CardTitle>
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-slate-600">{status.progress_pct}%</span>
            <button
              onClick={handleMinimize}
              className="text-muted-foreground hover:text-foreground"
              data-testid="onboarding-minimize-btn"
              aria-label="Minimera onboarding"
            >
              <ChevronUp size={16} />
            </button>
          </div>
        </div>
        <div className="w-full bg-muted rounded-full h-2 mt-3">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-500"
            style={{ width: `${status.progress_pct}%` }}
            data-testid="onboarding-progress-bar"
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {allItems.filter(i => i.status === 'completed').length} av {totalCount} klara
        </p>
      </CardHeader>
      <CardContent className="pt-5 space-y-5">
        {renderItems(status.setup_items, showSectionLabels ? 'Setup' : null)}
        {renderItems(status.operational_items, showSectionLabels ? 'Operativ start' : null)}
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;
